import React, { useEffect, useState } from 'react';

interface SpeedometerProps {
  score: number;
}

const Speedometer: React.FC<SpeedometerProps> = ({ score }) => {
  const [displayScore, setDisplayScore] = useState(0);

  useEffect(() => {
    // Animate the score counting up
    let start = 0;
    const duration = 1500; // 1.5s animation
    const increment = score / (duration / 16); // 60fps approx

    const timer = setInterval(() => {
      start += increment;
      if (start >= score) {
        setDisplayScore(score);
        clearInterval(timer);
      } else {
        setDisplayScore(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [score]);

  // Calculations for SVG arc
  const radius = 80;
  const stroke = 12;
  const normalizedRadius = radius - stroke * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  // We want a 240 degree arc (leaving bottom open)
  const arcLength = circumference * 0.75; 
  const strokeDashoffset = arcLength - (displayScore / 100) * arcLength;

  // Determine color based on score
  const getColor = (s: number) => {
    if (s < 50) return '#ef4444'; // Red-500
    if (s < 80) return '#eab308'; // Yellow-500
    return '#10b981'; // Emerald-500
  };

  const currentColor = getColor(displayScore);

  return (
    <div className="relative flex flex-col items-center justify-center p-6">
      <div className="relative w-64 h-64">
        {/* Drop Shadow Glow */}
        <div 
            className="absolute inset-0 rounded-full blur-2xl opacity-20 transition-colors duration-500"
            style={{ backgroundColor: currentColor }}
        ></div>

        <svg
          height="100%"
          width="100%"
          viewBox="0 0 200 200"
          className="transform rotate-[135deg] overflow-visible"
        >
          {/* Background Track */}
          <circle
            stroke="#1f2937" // gray-800
            strokeWidth={stroke}
            fill="transparent"
            r={normalizedRadius}
            cx="100"
            cy="100"
            strokeDasharray={`${arcLength} ${circumference}`}
            strokeLinecap="round"
          />
          {/* Progress Arc */}
          <circle
            stroke={currentColor}
            strokeWidth={stroke}
            fill="transparent"
            r={normalizedRadius}
            cx="100"
            cy="100"
            strokeDasharray={`${arcLength} ${circumference}`}
            style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.1s ease-out' }}
            strokeLinecap="round"
            className="drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]"
          />
        </svg>
        
        {/* Center Text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white pointer-events-none">
          <span className="text-6xl font-black tracking-tighter tabular-nums" style={{ textShadow: `0 0 20px ${currentColor}` }}>
            {displayScore}
          </span>
          <span className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-2">
            PUNTAJE
          </span>
        </div>
      </div>
    </div>
  );
};

export default Speedometer;